/**
 * Date/range/link helpers for the calendar frontend. All events are ET
 * (America/New_York); the API hands us pre-formatted labels + dayKeys, so the
 * only real timezone work here is computing "today in ET" and building
 * add-to-calendar URLs. Intl does the offset math — no TZ library.
 */

import type { EventDto, SubmissionInput } from './types';

const ET = 'America/New_York';

type EtParts = { y: number; mo: number; d: number; h: number; mi: number };

function etParts(ms: number): EtParts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: ET,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const map: Record<string, string> = {};
  for (const p of fmt.formatToParts(ms)) map[p.type] = p.value;
  return {
    y: Number(map.year),
    mo: Number(map.month),
    d: Number(map.day),
    h: Number(map.hour === '24' ? '0' : map.hour),
    mi: Number(map.minute),
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

export function dayKeyFor(y: number, mo: number, d: number): string {
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/** Today's ET calendar date. */
export function etToday(): { y: number; mo: number; d: number; dayKey: string; dow: number } {
  const p = etParts(Date.now());
  // Day-of-week for the ET date (UTC math on the plain date is safe).
  const dow = new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay();
  return { y: p.y, mo: p.mo, d: p.d, dayKey: dayKeyFor(p.y, p.mo, p.d), dow };
}

/** Add days to a plain calendar date (no timezone involved). */
export function addDays(
  y: number,
  mo: number,
  d: number,
  n: number
): { y: number; mo: number; d: number } {
  const dt = new Date(Date.UTC(y, mo - 1, d + n));
  return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

/** ET wall-clock → unix seconds (DST-safe, converges in 2 iterations). */
export function etToUnix(y: number, mo: number, d: number, h: number, mi: number): number {
  let ts = Date.UTC(y, mo - 1, d, h, mi, 0);
  for (let i = 0; i < 2; i++) {
    const p = etParts(ts);
    const wall = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, 0);
    const want = Date.UTC(y, mo - 1, d, h, mi, 0);
    ts += want - wall;
  }
  return Math.floor(ts / 1000);
}

/** 'YYYY-MM-DD' at end of day ET → unix seconds. */
export function dateStrToUnixEndOfDayEt(dateStr: string): number {
  const [y = 1970, mo = 1, d = 1] = dateStr.split('-').map(Number);
  return etToUnix(y, mo, d, 23, 59);
}

/** unix → 'YYYY-MM-DD' in ET (for date inputs). */
export function unixToDateStrEt(unix: number): string {
  const p = etParts(unix * 1000);
  return dayKeyFor(p.y, p.mo, p.d);
}

/** unix → compact ET local stamp for Google Calendar URLs: YYYYMMDDTHHMMSS. */
export function unixToEtCompact(unix: number): string {
  const p = etParts(unix * 1000);
  return `${p.y}${pad(p.mo)}${pad(p.d)}T${pad(p.h)}${pad(p.mi)}00`;
}

// ── Ranges (dayKey strings, inclusive) ─────────────────────

export type DateRange = { from: string; to: string };

/** Rolling 7 days from today (today … today+6). Rolling — not "through Sunday" —
 *  so the landing is never dead just because the calendar week is ending. */
export function rangeThisWeek(): DateRange {
  const t = etToday();
  const end = addDays(t.y, t.mo, t.d, 6);
  return { from: t.dayKey, to: dayKeyFor(end.y, end.mo, end.d) };
}

/** The following rolling 7 days (today+7 … today+13). */
export function rangeNextWeek(): DateRange {
  const t = etToday();
  const start = addDays(t.y, t.mo, t.d, 7);
  const end = addDays(t.y, t.mo, t.d, 13);
  return { from: dayKeyFor(start.y, start.mo, start.d), to: dayKeyFor(end.y, end.mo, end.d) };
}

/** Whole month for 'YYYY-MM'. */
export function rangeMonth(ym: string): DateRange {
  const [y = 1970, mo = 1] = ym.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return { from: dayKeyFor(y, mo, 1), to: dayKeyFor(y, mo, lastDay) };
}

export function currentYm(): string {
  const t = etToday();
  return `${t.y}-${pad(t.mo)}`;
}

export function shiftYm(ym: string, delta: number): string {
  const [y = 1970, mo = 1] = ym.split('-').map(Number);
  const dt = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}`;
}

export function ymLabel(ym: string): string {
  const [y = 1970, mo = 1] = ym.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(Date.UTC(y, mo - 1, 1));
}

// ── Grouping ───────────────────────────────────────────────

export interface DayGroup {
  dayKey: string;
  dateLabel: string;
  events: EventDto[];
}

export function groupByDay(events: EventDto[]): DayGroup[] {
  const groups: DayGroup[] = [];
  const byKey = new Map<string, DayGroup>();
  for (const e of events) {
    let g = byKey.get(e.dayKey);
    if (!g) {
      g = { dayKey: e.dayKey, dateLabel: e.dateLabel, events: [] };
      byKey.set(e.dayKey, g);
      groups.push(g);
    }
    g.events.push(e);
  }
  return groups;
}

/** Split pinned spotlights from the regular flow (spotlights stay in both? No — pinned only). */
export function splitSpotlights(events: EventDto[]): { spotlights: EventDto[]; rest: EventDto[] } {
  const spotlights = events.filter((e) => e.spotlight);
  const rest = events.filter((e) => !e.spotlight);
  return { spotlights, rest };
}

// ── Label parsing (DTO → form prefill) ─────────────────────

/** '9:00 AM' → '09:00' (24h). */
export function parse12hTo24h(label: string): string {
  const m = label.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return '';
  let h = Number(m[1]) % 12;
  if ((m[3] ?? '').toUpperCase() === 'PM') h += 12;
  return `${pad(h)}:${m[2]}`;
}

/** EventDto → SubmissionInput for edit forms. */
export function dtoToSubmission(e: EventDto): SubmissionInput {
  return {
    title: e.title,
    date: e.dayKey,
    startTime: e.iso.length >= 16 ? e.iso.slice(11, 16) : parse12hTo24h(e.timeLabel),
    endTime: e.endTimeLabel ? parse12hTo24h(e.endTimeLabel) : '',
    locationName: e.locationName,
    address: e.address,
    area: e.area,
    cost: e.cost,
    isFree: e.isFree,
    category: e.category,
    organizerName: e.organizerName,
    organizerWebsite: e.organizerWebsite,
    registerUrl: e.registerUrl,
    description: e.description,
  };
}

/** 'Wed, Jul 8' → { dow: 'Wed', mon: 'Jul', day: '8' } for the big date chip. */
export function splitDateLabel(label: string): { dow: string; mon: string; day: string } {
  const m = label.match(/^(\w+),\s+(\w+)\s+(\d+)$/);
  if (!m) return { dow: '', mon: '', day: label };
  return { dow: m[1] ?? '', mon: m[2] ?? '', day: m[3] ?? label };
}

// ── Outbound links ─────────────────────────────────────────

export function mapsUrl(e: EventDto): string {
  const q = [e.locationName, e.address].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function googleCalUrl(e: EventDto): string {
  const start = unixToEtCompact(e.startsAt);
  const end = unixToEtCompact(e.endsAt ?? e.startsAt + 3600);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${start}/${end}`,
    ctz: ET,
    details: e.description + (e.registerUrl ? `\n\nRegister: ${e.registerUrl}` : ''),
    location: [e.locationName, e.address].filter(Boolean).join(', '),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function outlookUrl(e: EventDto): string {
  const endIso = e.endsAt
    ? new Date(e.endsAt * 1000).toISOString()
    : new Date((e.startsAt + 3600) * 1000).toISOString();
  const params = new URLSearchParams({
    subject: e.title,
    startdt: new Date(e.startsAt * 1000).toISOString(),
    enddt: endIso,
    location: [e.locationName, e.address].filter(Boolean).join(', '),
    body: e.description,
    path: '/calendar/action/compose',
    rru: 'addevent',
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function linkedInShareUrl(url: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

export function xShareUrl(url: string, text: string): string {
  return `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
}

// ── Misc ───────────────────────────────────────────────────

export function countWords(text: string): number {
  const t = text.trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
}

export function formatUnixDate(value: number): string {
  if (!value) return '—';
  const ms = value > 1e12 ? value : value * 1000; // tolerate ms or seconds
  return new Intl.DateTimeFormat('en-US', {
    timeZone: ET,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(ms);
}
